import { useCallback, useEffect } from 'react';
import { DriveStep } from 'driver.js';
import { createTour, hasSeenTour, markTourAsSeen } from '../utils/productTours';
import { useUserStore } from '/src/context';

interface UseProductTourOptions {
  tourName: string;
  steps: DriveStep[];
  autoStart?: boolean;
  showOnce?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config?: any;
}

export const useProductTour = ({
  tourName,
  steps,
  autoStart = false,
  showOnce = true,
  config,
}: UseProductTourOptions) => {
  const { isFuseAdmin } = useUserStore();

  const startTour = useCallback(() => {
    if (isFuseAdmin) return;
    const tour = createTour(steps, {
      ...config,
      onDestroyed: () => {
        if (showOnce) {
          markTourAsSeen(tourName);
        }

        if (config?.onDestroyed) {
          config.onDestroyed();
        }
      },
      onNextClick: (element, step, options) => {
        const currentStepIndex = options.state.activeIndex ?? 0;

        if (currentStepIndex === steps.length - 1) {
          tour.destroy();
        } else {
          tour.moveNext();
        }
        config?.onNextClick?.(element, step, options);
      },
    });
    tour.drive();
  }, [steps, config, tourName, showOnce, isFuseAdmin]);

  useEffect(() => {
    if (autoStart && (!showOnce || !hasSeenTour(tourName))) {
      const timer = setTimeout(() => {
        startTour();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoStart, showOnce, tourName, startTour]);

  return {
    startTour,
    hasSeenTour: hasSeenTour(tourName),
  };
};
